// The Workspace screens put together for cloud's host (main.tsx): the part Me
// lists, the rail, the way to the other place's window, the routes, the
// sidebar around Me's pages opened from a workspace, and the handlers of the
// team's events.
import type { AnyRoute } from "@tanstack/react-router";
import {
  type DockExtension,
  defineScreenPart,
  type LiveTransport,
  type MeSidebarSwap,
  type ScreenPart,
  type ShellRail,
} from "@trameria/ui-core";
import type { ComponentType } from "react";
import type { WorkspaceHost } from "./context";
import { workspaceDock } from "./dock";
import { useWorkspaceSidebarSwap } from "./frame";
import { MePlaceFollower } from "./me-place";
import { useWorkspaceRail, WorkspaceRail } from "./rail";
import { workspaceMeHandlers, workspaceTeamHandlers } from "./realtime";
import { type WorkspaceRouteOptions, workspaceRoutes } from "./routes";
import {
  orderSections,
  type TeamRealtimeHandlers,
  type WorkspaceSection,
} from "./sections";
import type { Windows } from "./windows";

export interface WorkspaceScreenOptions {
  /** The sections the parts add to the Workspace sidebar. */
  sections?: readonly WorkspaceSection[];
  live: LiveTransport;
  windows: Windows;
}

export interface WorkspaceScreen {
  /** For `createMeApp({ parts })`: Me's events it hears, and what is kept across reloads. */
  part: ScreenPart;
  Rail: ComponentType;
  /** The rail as data (Me's extension `useRail`). */
  useRail(): ShellRail;
  /** Me's extension `useSidebar`: a workspace's sidebar around Me's pages opened from it. */
  useSidebar(): MeSidebarSwap | null;
  /** The workspace's side of the phone's Dock. */
  dock: DockExtension;
  openTarget(href: string, place: string): boolean;
  routes(root: AnyRoute, auth: Omit<WorkspaceRouteOptions, "host">): AnyRoute[];
  /** For the team stream: this part's handlers and the sections'. */
  teamHandlers: readonly TeamRealtimeHandlers[];
}

export function workspaceScreen(
  options: WorkspaceScreenOptions,
): WorkspaceScreen {
  const sections = orderSections(options.sections ?? []);
  const host: WorkspaceHost = {
    sections,
    windows: options.windows,
    live: options.live,
  };
  return {
    part: defineScreenPart({
      id: "workspace",
      // Which workspace Me's page is shown in (me-place.tsx).
      Background: MePlaceFollower,
      realtime: workspaceMeHandlers,
      // What draws a workspace's frame at once after a reload: the rail, the
      // workspace as I see it, its spaces.
      persist: (key) =>
        (key[0] === "team" && key[1] === "workspaces") ||
        (key[0] === "ws" && (key[2] === "view" || key[2] === "spaces")),
    }),
    Rail: WorkspaceRail,
    useRail: useWorkspaceRail,
    useSidebar: () => useWorkspaceSidebarSwap(host),
    dock: workspaceDock(host),
    openTarget: (href, place) => options.windows.open(href, place),
    routes: (root, auth) => workspaceRoutes(root, { host, ...auth }),
    teamHandlers: [
      workspaceTeamHandlers,
      ...sections.flatMap((s) => (s.realtime ? [s.realtime] : [])),
    ],
  };
}
